import type { ProductDrawerTab } from "@/components/products/product-inventory-drawer";
import { TablePagination } from "@/components/tables/_components/table-pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import type { Product } from "@/services/types/products";
import { format } from "date-fns";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  MoreVertical,
  Search,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState } from "react";

function toNumber(value: number | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatQuantity(value: number | null | undefined): string {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(toNumber(value));
}

function formatCurrency(value: number | null | undefined): string {
  const amount = toNumber(value);
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return format(date, "MMM d, yyyy");
  } catch {
    return "-";
  }
}

type ProductStatus = "in_stock" | "out_of_stock" | "low_stock";

function getProductBrandNames(product: Product): string {
  const brandNames = Array.from(
    new Set(
      product.product_brands
        .map((brand) => brand.brand_name.trim())
        .filter(Boolean),
    ),
  );
  return brandNames.join(" · ");
}

function getDisplayBaseUom(product: Product): string {
  return product.base_stock_uom_code || "";
}

function getProductStatus(product: Product): ProductStatus {
  if (toNumber(product.quantity_available) > 0) {
    return "in_stock";
  }
  return "out_of_stock";
}

function getStatusLabel(status: ProductStatus): string {
  switch (status) {
    case "in_stock":
      return "In stock";
    case "low_stock":
      return "Low stock";
    case "out_of_stock":
      return "Out of stock";
  }
}

function getStatusBadgeClassName(status: ProductStatus): string {
  switch (status) {
    case "in_stock":
      return "bg-emerald-50 text-emerald-700 hover:bg-emerald-50";
    case "low_stock":
      return "bg-amber-50 text-amber-700 hover:bg-amber-50";
    case "out_of_stock":
      return "bg-rose-50 text-rose-700 hover:bg-rose-50";
  }
}

function getStatusDotClassName(status: ProductStatus): string {
  switch (status) {
    case "in_stock":
      return "bg-emerald-500";
    case "low_stock":
      return "bg-amber-500";
    case "out_of_stock":
      return "bg-rose-500";
  }
}

function ProductRowActions({
  onOpenProduct,
  onDuplicate,
  onDelete,
  canManage,
}: {
  onOpenProduct: (tab: ProductDrawerTab) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  canManage: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full text-muted-foreground"
        >
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">Open product actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem>Edit SKU details</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onOpenProduct("stock")}>
          Edit stock
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onOpenProduct("units")}>
          Configure UOM
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onOpenProduct("history")}>
          View stock history
        </DropdownMenuItem>
        {canManage ? (
          <>
            <DropdownMenuItem onSelect={onDuplicate}>Duplicate</DropdownMenuItem>
            <DropdownMenuItem onSelect={onDelete}>Delete</DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AllProductsTable({
  data,
  totalCount,
  missingBaseUomCount,
  currentPage,
  pageCount,
  onPageChange,
  searchValue,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  missingUomOnly,
  onMissingUomOnlyChange,
  isLoading,
  onOpenProduct,
  onDuplicateProduct,
  onDeleteProduct,
  canManage,
  isDeletingProduct,
  sortField,
  sortDirection,
  onSort,
}: {
  data: Product[];
  totalCount: number;
  missingBaseUomCount: number;
  currentPage: number;
  pageCount: number;
  onPageChange: (pageIndex: number) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  statusFilter: "all" | ProductStatus;
  onStatusFilterChange: (value: "all" | "in_stock" | "out_of_stock") => void;
  missingUomOnly: boolean;
  onMissingUomOnlyChange: (value: boolean) => void;
  isLoading: boolean;
  onOpenProduct: (productId: number, tab: ProductDrawerTab) => void;
  onDuplicateProduct: (product: Product) => void;
  onDeleteProduct: (product: Product) => void;
  canManage: boolean;
  isDeletingProduct: boolean;
  sortField: "status" | "price" | "date" | null;
  sortDirection: "asc" | "desc";
  onSort: (field: "status" | "price" | "date") => void;
}) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  const sortButton = (field: "status" | "price" | "date", label: string) => {
    const active = sortField === field;
    const Icon = active
      ? sortDirection === "asc"
        ? ArrowUp
        : ArrowDown
      : ArrowUpDown;
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1"
        aria-label={`Sort by ${label}`}
        onClick={() => onSort(field)}
      >
        {label}
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    );
  };

  return (
    <>
      <div
        className={cn(
          "rounded-2xl border bg-card",
          isMobile && "border-0 bg-transparent",
        )}
      >
        <div className="flex flex-col gap-3 border-b-0 px-4 py-4 md:flex-row md:items-center md:justify-between md:border-b md:px-6">
          <div className="relative w-full md:w-[360px]">
            <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products, SKU or brand"
              value={searchValue}
              onChange={(event) => onSearchChange(event.target.value)}
              className="h-12 w-full rounded-xl bg-background pl-9"
            />
          </div>
          <div className="hidden w-full md:block md:w-[180px]">
            <Select
              value={statusFilter}
              onValueChange={(value: "all" | "in_stock" | "out_of_stock") =>
                onStatusFilterChange(value)
              }
            >
              <SelectTrigger className="h-10 w-full rounded-lg">
                <SelectValue placeholder="All status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="in_stock">In Stock</SelectItem>
                <SelectItem value="out_of_stock">Out of Stock</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-b px-4 pt-0 pb-4 md:flex-row md:items-center md:justify-between md:px-6 md:py-4">
          <div className="flex flex-wrap items-center gap-3 text-muted-foreground text-sm">
            {searchValue || statusFilter !== "all"
              ? `Showing ${totalCount} products`
              : `Showing all ${totalCount} products`}
            {!isMobile ? (
              <button
                type="button"
                aria-pressed={missingUomOnly}
                className={cn(
                  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 font-medium text-xs transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 focus-visible:ring-offset-2",
                  missingUomOnly
                    ? "border-rose-300 bg-rose-200 text-rose-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] ring-1 ring-rose-300/70"
                    : "border-rose-100 bg-rose-50 text-rose-700 hover:border-rose-200 hover:bg-rose-100",
                )}
                onClick={() => onMissingUomOnlyChange(!missingUomOnly)}
              >
                {missingUomOnly ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <AlertCircle className="h-3.5 w-3.5" />
                )}
                {missingBaseUomCount} SKU missing UOM setup
              </button>
            ) : null}
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">
            Loading products...
          </div>
        ) : data.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No products found
          </div>
        ) : isMobile ? (
          <div className="space-y-4 px-0 py-3">
            {data.map((product) => {
              const status = getProductStatus(product);
              const brandNames = getProductBrandNames(product);
              const displayBaseUom = getDisplayBaseUom(product);

              return (
                <div
                  key={product.id || product.sku}
                  className="rounded-3xl border border-border/70 bg-background px-4 py-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-[17px] leading-6">
                        {product.name || "Unnamed Product"}
                      </div>
                      {brandNames ? (
                        <div className="mt-1 truncate text-[14px] text-muted-foreground">
                          {brandNames}
                        </div>
                      ) : null}
                    </div>
                    <ProductRowActions
                      onOpenProduct={(tab) => onOpenProduct(product.id, tab)}
                      onDuplicate={() => onDuplicateProduct(product)}
                      onDelete={() => setProductToDelete(product)}
                      canManage={canManage}
                    />
                  </div>

                  <div className="mt-3 flex justify-end">
                    <Badge
                      className={cn(
                        "rounded-full px-3 py-1 font-medium text-xs",
                        getStatusBadgeClassName(status),
                      )}
                    >
                      <span
                        className={`mr-2 h-2 w-2 rounded-full ${getStatusDotClassName(status)}`}
                      />
                      {getStatusLabel(status)}
                    </Badge>
                  </div>

                  <div className="my-4 h-px bg-border/70" />

                  <div className="grid grid-cols-[auto_auto_1fr_auto] items-center gap-3 text-sm">
                    <span className="text-[14px] text-muted-foreground">
                      {product.sku}
                    </span>
                    <div className="min-w-0">
                      {displayBaseUom ? (
                        <span className="inline-flex rounded-md border border-border bg-background px-2.5 py-1 text-[14px] text-muted-foreground">
                          {displayBaseUom}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="inline-flex whitespace-nowrap rounded-md border border-border bg-muted/40 px-2.5 py-1 text-[14px] text-muted-foreground hover:bg-muted/80"
                          onClick={() => {
                            onOpenProduct(product.id, "units");
                          }}
                        >
                          + Set Up UOM
                        </button>
                      )}
                    </div>
                    <div className="min-w-0 text-right font-medium text-[15px] text-foreground">
                      {formatQuantity(product.quantity_available)}{" "}
                      {displayBaseUom}
                    </div>
                    <div className="text-right text-[14px] text-muted-foreground">
                      {formatCurrency(product.price)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[960px]">
              <div className="grid grid-cols-[2fr_1.2fr_1fr_1fr_1fr_1fr_1.4fr_72px] border-b bg-muted/60 font-medium text-sm">
                <div className="px-4 py-3">Product</div>
                <div className="px-4 py-3">SKU ID</div>
                <div className="px-4 py-3">Base UOM</div>
                <div className="px-4 py-3">Total Stock</div>
                <div className="px-4 py-3">{sortButton("status", "Status")}</div>
                <div className="px-4 py-3">{sortButton("price", "Price")}</div>
                <div className="px-4 py-3">{sortButton("date", "Updated")}</div>
                <div className="px-4 py-3 text-right">Actions</div>
              </div>

              {data.map((product) => {
                const status = getProductStatus(product);
                const brandNames = getProductBrandNames(product);
                const displayBaseUom = getDisplayBaseUom(product);

                return (
                  <div
                    key={product.id || product.sku}
                    className="grid grid-cols-[2fr_1.2fr_1fr_1fr_1fr_1fr_1.4fr_72px] items-center border-b text-sm transition-colors hover:bg-muted/30"
                  >
                    <div className="px-4 py-3">
                      <div className="font-medium">
                        {product.name || "Unnamed Product"}
                      </div>
                      {brandNames ? (
                        <div className="mt-1 text-muted-foreground text-sm">
                          {brandNames}
                        </div>
                      ) : null}
                    </div>
                    <div className="px-4 py-3 text-muted-foreground">
                      {product.sku}
                    </div>
                    <div className="px-4 py-3">
                      {displayBaseUom ? (
                        <span className="inline-flex rounded-md border border-border bg-background px-3 py-1.5 text-muted-foreground text-sm">
                          {displayBaseUom}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="inline-flex whitespace-nowrap rounded-md border border-border bg-muted/40 px-3 py-1.5 text-muted-foreground text-sm hover:bg-muted/80"
                          onClick={() => {
                            onOpenProduct(product.id, "units");
                          }}
                        >
                          + Set Up UOM
                        </button>
                      )}
                    </div>
                    <div className="px-4 py-3 text-muted-foreground">
                      {formatQuantity(product.quantity_available)}
                    </div>
                    <div className="px-4 py-3">
                      <Badge className={getStatusBadgeClassName(status)}>
                        <span
                          className={`mr-2 h-2 w-2 rounded-full ${getStatusDotClassName(status)}`}
                        />
                        {getStatusLabel(status)}
                      </Badge>
                    </div>
                    <div className="px-4 py-3 text-muted-foreground">
                      {formatCurrency(product.price)}
                    </div>
                    <div className="px-4 py-3 text-muted-foreground">
                      {formatDate(product.updated_at)}
                    </div>
                    <div className="flex justify-end px-4 py-3">
                      <ProductRowActions
                        onOpenProduct={(tab) => onOpenProduct(product.id, tab)}
                        onDuplicate={() => onDuplicateProduct(product)}
                        onDelete={() => setProductToDelete(product)}
                        canManage={canManage}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <TablePagination
          currentPage={currentPage}
          pageCount={pageCount}
          canPreviousPage={currentPage > 0}
          canNextPage={currentPage < pageCount - 1}
          onPreviousPage={() => onPageChange(Math.max(currentPage - 1, 0))}
          onNextPage={() =>
            onPageChange(Math.min(currentPage + 1, pageCount - 1))
          }
          onPageChange={onPageChange}
        />
      </div>
      <Dialog
        open={Boolean(productToDelete)}
        onOpenChange={(open) => !open && setProductToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete product?</DialogTitle>
            <DialogDescription>
              This permanently deletes {productToDelete?.name || productToDelete?.sku}.
              Only unused products can be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isDeletingProduct}
              onClick={() => {
                if (productToDelete) onDeleteProduct(productToDelete);
                setProductToDelete(null);
              }}
            >
              {isDeletingProduct ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
